import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { X, Info, AlertTriangle, Paperclip } from "lucide-react";

export const MAROON = "#7F1D2D";
export const inpL = "h-10 px-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D] w-full";

export const TYPE_LABEL = { izin_pribadi: "Izin Pribadi", sakit: "Sakit", cuti: "Cuti", terlambat: "Terlambat", pulang_awal: "Pulang Lebih Awal" };
export const DUR_LABEL = { full: "Sehari Penuh", half_start: "Setengah Hari Awal", half_end: "Setengah Hari Akhir", hours: "Jam Tertentu" };
export const ST_LABEL = { menunggu: "Menunggu", disetujui: "Disetujui", ditolak: "Ditolak", dibatalkan: "Dibatalkan" };
export const ST_CHIP = { menunggu: "bg-amber-50 text-amber-600", disetujui: "bg-emerald-50 text-emerald-600", ditolak: "bg-red-50 text-red-600", dibatalkan: "bg-slate-100 text-slate-400" };
export const TYPE_CHIP = { izin_pribadi: "bg-sky-50 text-sky-600", sakit: "bg-rose-50 text-rose-600", cuti: "bg-violet-50 text-violet-600", terlambat: "bg-amber-50 text-amber-600", pulang_awal: "bg-cyan-50 text-cyan-600" };

export function LeaveDaysPreview({ preview, tid = "lv" }) {
  if (!preview) return null;
  const workdays = (preview.days || []).filter((d) => d.workday);
  return (
    <div data-testid={`${tid}-preview`} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pratinjau Shift & Dampak Izin</div>
      {preview.needs_schedule && (
        <div data-testid={`${tid}-needs-schedule`} className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Jadwal shift karyawan belum lengkap — lengkapi jadwal di menu Shift Kerja sebelum izin diproses.
        </div>
      )}
      {(preview.errors || []).map((e, i) => (
        <div key={i} data-testid={`${tid}-error-${i}`} className="flex items-start gap-2 text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {e}
        </div>
      ))}
      {workdays.length > 0 && (
        <div className="space-y-1.5">
          {workdays.map((d) => (
            <div key={d.date} data-testid={`${tid}-day-${d.date}`} className="bg-white rounded-lg border border-slate-100 px-2.5 py-2">
              <div className="flex justify-between text-[11px] font-semibold text-slate-700">
                <span>{formatDateID(d.date)}</span><span className="text-slate-400">Shift {d.shift}{d.break ? ` • Istirahat ${d.break}` : ""}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-1 text-[10px]">
                <div className="text-red-500">Jam izin: <b>{d.leave_start ? `${d.leave_start} - ${d.leave_end}` : "—"}</b> ({((d.leave_minutes || 0) / 60).toFixed(1)} jam)</div>
                <div className="text-emerald-600 text-right">Wajib hadir: <b>{d.required_start ? `${d.required_start} - ${d.required_end}` : "Dibebaskan"}</b></div>
              </div>
            </div>
          ))}
          <div className="flex justify-between text-[11px] font-bold text-slate-600 px-1 pt-1">
            <span>{preview.workdays} hari kerja terdampak • {preview.net_hours} jam izin</span>
            <span data-testid={`${tid}-units`} className="text-[#7F1D2D]">Saldo terpakai: {preview.leave_units} hari</span>
          </div>
          {preview.balance && (
            <div className="text-[10px] text-slate-400 px-1">Sisa saldo tahun ini: <b className="text-slate-600">{preview.balance.remaining}</b> dari {preview.balance.quota} hari</div>
          )}
        </div>
      )}
      {!workdays.length && !(preview.errors || []).length && !preview.needs_schedule && (
        <div className="text-[11px] text-slate-400 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Rentang ini hanya berisi hari libur — tidak ada hari kerja terdampak.</div>
      )}
    </div>
  );
}

export default function LeaveForm({ emps = null, onClose, onSaved, tid = "leavef" }) {
  const [meta, setMeta] = useState(null);
  const [empId, setEmpId] = useState("");
  const [type, setType] = useState("izin_pribadi");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [duration, setDuration] = useState("full");
  const [h1, setH1] = useState("");
  const [h2, setH2] = useState("");
  const [reason, setReason] = useState("");
  const [proof, setProof] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/leave/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!start || !end || (emps && !empId)) { setPreview(null); return; }
    const t = setTimeout(() => {
      api.get("/leave/preview", { params: { user_id: empId || undefined, type, duration, start_date: start, end_date: end, hours_start: h1 || undefined, hours_end: h2 || undefined } })
        .then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [empId, type, duration, start, end, h1, h2, emps]);

  const submit = async () => {
    if (emps && !empId) return toast.error("Pilih karyawan");
    if (!start || !end) return toast.error("Pilih tanggal mulai dan selesai");
    if (reason.trim().length < 3) return toast.error("Alasan wajib diisi");
    if (duration === "hours" && (!h1 || !h2)) return toast.error("Pilih jam mulai dan selesai");
    if (preview && (preview.errors || []).length) return toast.error(preview.errors[0]);
    setBusy(true);
    try {
      let proof_url = null;
      if (proof) {
        const fd = new FormData();
        fd.append("file", proof);
        const up = await api.post("/uploads", fd);
        proof_url = up.data.url;
      }
      await api.post("/leave", { user_id: empId || undefined, type, duration, start_date: start, end_date: end, hours_start: h1 || undefined, hours_end: h2 || undefined, reason: reason.trim(), proof_url });
      toast.success("Pengajuan izin terkirim — menunggu keputusan HR");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim pengajuan");
    } finally { setBusy(false); }
  };

  const blocked = busy || (preview && ((preview.errors || []).length > 0 || preview.needs_schedule));
  return (
    <div data-testid={`${tid}-modal`} className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="font-display text-base font-bold text-slate-900">Catat Izin</h3>
          <button data-testid={`${tid}-close`} onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {emps && (
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Karyawan</label>
              <select data-testid={`${tid}-emp`} value={empId} onChange={(e) => setEmpId(e.target.value)} className={inpL}>
                <option value="">— Pilih karyawan —</option>
                {emps.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Jenis Izin</label>
              <select data-testid={`${tid}-type`} value={type} onChange={(e) => setType(e.target.value)} className={inpL}>
                {(meta?.types || Object.entries(TYPE_LABEL).map(([id, label]) => ({ id, label }))).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Durasi</label>
              <select data-testid={`${tid}-duration`} value={duration} onChange={(e) => setDuration(e.target.value)} className={inpL}>
                {(meta?.durations || Object.entries(DUR_LABEL).map(([id, label]) => ({ id, label }))).map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Tanggal Mulai</label>
              <input data-testid={`${tid}-start`} type="date" value={start} onChange={(e) => { setStart(e.target.value); if (duration !== "full") setEnd(e.target.value); }} className={inpL} />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Tanggal Selesai</label>
              <input data-testid={`${tid}-end`} type="date" value={end} min={start} disabled={duration !== "full"} onChange={(e) => setEnd(e.target.value)} className={inpL} />
            </div>
          </div>
          {duration === "hours" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Jam Mulai</label>
                <input data-testid={`${tid}-h1`} type="time" value={h1} onChange={(e) => setH1(e.target.value)} className={inpL} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Jam Selesai</label>
                <input data-testid={`${tid}-h2`} type="time" value={h2} onChange={(e) => setH2(e.target.value)} className={inpL} />
              </div>
            </div>
          )}
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Alasan <span className="text-red-500">*</span></label>
            <textarea data-testid={`${tid}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Tulis alasan pengajuan..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Lampiran (opsional)</label>
            <label data-testid={`${tid}-proof`} className="flex items-center gap-2 h-10 px-3 rounded-xl border border-dashed border-slate-300 text-xs text-slate-500 cursor-pointer hover:border-[#7F1D2D] hover:text-[#7F1D2D] transition-colors">
              <Paperclip className="w-3.5 h-3.5" /> {proof ? proof.name : "Pilih foto / video pendukung..."}
              <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => setProof(e.target.files?.[0] || null)} />
            </label>
          </div>
          <LeaveDaysPreview preview={preview} tid={tid} />
          <button data-testid={`${tid}-submit`} onClick={submit} disabled={blocked}
            className="w-full h-11 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-40" style={{ background: MAROON }}>
            {busy ? "Mengirim..." : "Kirim Pengajuan"}
          </button>
          <p className="text-[10px] text-slate-400 text-center flex items-center justify-center gap-1"><Info className="w-3 h-3" /> Pengajuan menunggu belum mengubah absensi atau gaji.</p>
        </div>
      </div>
    </div>
  );
}
